import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Users, Plus, Mail, X, AtSign, Mic, Globe, Copy, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Workspaces() {
  const [workspaces, setWorkspaces] = useState([]);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState({ members: [], pending_invites: [] });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const ws = await api.listWorkspaces();
    setWorkspaces(ws);
    if (ws.length && !active) setActive(ws[0]);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (active) api.workspaceMembers(active.workspace_id).then(setMembers);
  }, [active]);

  const createWs = async () => {
    if (!newWorkspaceName.trim()) return;
    const ws = await api.createWorkspace(newWorkspaceName);
    setNewWorkspaceName("");
    setShowCreate(false);
    await load();
    setActive(ws);
    toast.success("Workspace created");
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Email required");
    try {
      const r = await api.inviteMember(active.workspace_id, inviteEmail, inviteRole);
      setInviteEmail("");
      if (r.email_sent === false) {
        toast.warning("Invite saved but email not sent. Share the token below.");
      } else {
        toast.success("Invite sent");
      }
      const m = await api.workspaceMembers(active.workspace_id);
      setMembers(m);
    } catch (e) {
      toast.error("Invite failed");
    }
  };

  const removeMember = async (uid) => {
    await api.removeMember(active.workspace_id, uid);
    const m = await api.workspaceMembers(active.workspace_id);
    setMembers(m);
    toast.success("Removed");
  };

  return (
    <div data-testid="workspaces-page" className="max-w-6xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-eyebrow mb-3">Team</div>
          <h1 className="font-display font-black text-5xl tracking-tighter">Workspaces</h1>
          <p className="text-[#52525B] mt-3">Invite teammates to collaborate on brands. Each workspace shares its brands and content.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-workspace-button" className="bg-[#0A0A0B] text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]">
          <Plus size={16} /> New Workspace
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <div className="text-eyebrow mb-2">My workspaces</div>
          {workspaces.map((w) => (
            <button
              key={w.workspace_id}
              onClick={() => setActive(w)}
              data-testid={`workspace-${w.workspace_id}`}
              className={`w-full text-left p-4 border ${active?.workspace_id === w.workspace_id ? "border-[#0A0A0B] bg-[#F4F5F6]" : "border-[#E2E4E9] hover:border-black"}`}
            >
              <div className="font-display font-bold text-lg tracking-tight">{w.name}</div>
              <div className="text-xs mono uppercase text-[#A1A1AA] mt-1">{w.is_personal ? "Personal" : "Team"} · {w.member_count} member{w.member_count !== 1 ? "s" : ""} · {w.role}</div>
            </button>
          ))}
        </div>

        <div className="md:col-span-2">
          {active && (
            <>
              <SenderPersona workspace={active} onSaved={async () => { await load(); }} />

              <div className="mb-6">
                <div className="text-eyebrow mb-2">Invite teammate</div>
                <div className="flex gap-2">
                  <input
                    placeholder="email@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    data-testid="invite-email-input"
                    className="flex-1 border border-[#E2E4E9] focus:border-black p-3 outline-none"
                  />
                  <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                          data-testid="invite-role-select"
                          className="border border-[#E2E4E9] focus:border-black p-3 outline-none">
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button onClick={invite} data-testid="invite-send-button" className="bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7] flex items-center gap-2">
                    <Mail size={14} /> Invite
                  </button>
                </div>
              </div>

              <div className="text-eyebrow mb-3">Members ({members.members?.length || 0})</div>
              <div className="space-y-2 mb-6">
                {members.members?.map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between border border-[#E2E4E9] p-3" data-testid={`member-${m.user_id}`}>
                    <div className="flex items-center gap-3">
                      {m.picture ? <img src={m.picture} alt="" className="w-8 h-8 border border-black" /> : <div className="w-8 h-8 bg-[#F4F5F6] border border-black flex items-center justify-center text-xs mono">{(m.name || m.email || "?")[0]}</div>}
                      <div>
                        <div className="font-semibold">{m.name || m.email}</div>
                        <div className="text-xs mono text-[#A1A1AA] uppercase">{m.role}</div>
                      </div>
                    </div>
                    {m.role !== "owner" && (
                      <button onClick={() => removeMember(m.user_id)} data-testid={`remove-${m.user_id}`} className="text-[#A1A1AA] hover:text-[#E11D48]"><X size={16} /></button>
                    )}
                  </div>
                ))}
              </div>

              {members.pending_invites?.length > 0 && (
                <>
                  <div className="text-eyebrow mb-3">Pending invites</div>
                  <div className="space-y-2">
                    {members.pending_invites.map((inv) => (
                      <div key={inv.invite_id} className="border border-[#E2E4E9] p-3" data-testid={`invite-${inv.invite_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{inv.email}</div>
                            <div className="text-xs mono text-[#A1A1AA] uppercase">{inv.role} · {(inv.created_at || "").slice(0, 10)}</div>
                          </div>
                          <div className="text-xs mono text-[#52525B]">token: {inv.token.slice(0, 10)}…</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border-2 border-black w-full max-w-md p-8 brutal-shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-3xl tracking-tight">New workspace</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <input
              placeholder="e.g. Acme Agency"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              data-testid="workspace-name-input"
              className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none"
            />
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-5 py-3 text-sm border border-black">Cancel</button>
              <button onClick={createWs} data-testid="create-workspace-confirm" className="px-5 py-3 text-sm bg-[#0A0A0B] text-white">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SenderPersona({ workspace, onSaved }) {
  const [name, setName] = React.useState(workspace.sender_name || "");
  const [email, setEmail] = React.useState(workspace.sender_email || "");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    setName(workspace.sender_name || "");
    setEmail(workspace.sender_email || "");
  }, [workspace.workspace_id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateWorkspace(workspace.workspace_id, { sender_name: name, sender_email: email });
      toast.success("Sender persona saved");
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const preview = (name?.trim() && email?.trim()) ? `${name.trim()} <${email.trim()}>` : (email || "(default)");

  return (
    <div className="mb-8 border border-[#E2E4E9] p-4" data-testid="sender-persona-block">
      <div className="flex items-center gap-2 mb-2">
        <AtSign size={14} />
        <span className="text-eyebrow">Sender persona</span>
      </div>
      <p className="text-xs text-[#52525B] mb-3">How emails from this workspace appear in the recipient's inbox. The local-part (before @) and display name are yours; the domain must be one you've verified in Resend.</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          placeholder="Display name (e.g. Acme Daily)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="sender-name-input"
          className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none"
        />
        <input
          placeholder="acme@vitalitysubstack.org"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="sender-email-input"
          className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none font-mono"
        />
      </div>
      <div className="mt-2 text-xs mono text-[#52525B]">Preview: <span className="text-[#0A0A0B]">{preview}</span></div>
      <button onClick={save} disabled={busy} data-testid="save-sender-button" className="mt-2 px-4 py-2 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-50">
        {busy ? "Saving…" : "Save persona"}
      </button>
      <BuzzsproutConfig workspace={workspace} />
      <WhiteLabelDomain workspace={workspace} />
    </div>
  );
}

function WhiteLabelDomain({ workspace }) {
  const [cfg, setCfg] = React.useState(null);
  const [domain, setDomain] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const refresh = async () => {
    try { setCfg(await api.getWhitelabelDomain(workspace.workspace_id)); } catch (_) { setCfg({ configured: false }); }
  };

  React.useEffect(() => { refresh(); }, [workspace.workspace_id]);

  const add = async () => {
    if (!domain.trim()) return;
    setBusy(true);
    try {
      const r = await api.addWhitelabelDomain(workspace.workspace_id, domain.trim().toLowerCase());
      setCfg(r);
      toast.success("Domain added. Add the DNS records, then click Verify.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.verifyWhitelabelDomain(workspace.workspace_id);
      await refresh();
      if (r.status === "verified") toast.success("Verified! All future sends use your domain.");
      else toast.message("Still pending. DNS can take 5-30 minutes to propagate.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this custom domain? You'll go back to vitalitysubstack.org for sends.")) return;
    setBusy(true);
    try {
      await api.deleteWhitelabelDomain(workspace.workspace_id);
      setCfg({ configured: false });
      toast.success("Removed.");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <div className="mt-6 border-t border-[#E2E4E9] pt-5" data-testid="whitelabel-domain-block">
      <div className="flex items-center gap-2 mb-2">
        <Globe size={14}/>
        <span className="text-eyebrow">White-label sending domain (Mogul)</span>
      </div>
      <p className="text-xs text-[#52525B] mb-3 leading-relaxed">
        Send Substack posts from <strong>your</strong> domain (e.g., <code className="mono">mail.yourbrand.com</code>) instead of vitalitysubstack.org.
        After adding, copy the DNS records into your DNS provider (Cloudflare, GoDaddy, etc), then click Verify.
      </p>

      {!cfg.configured ? (
        <div className="flex gap-2">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="mail.yourbrand.com"
            data-testid="whitelabel-domain-input"
            className="flex-1 border-2 border-black p-2 text-sm mono"
          />
          <button onClick={add} disabled={busy} data-testid="whitelabel-add-button" className="bg-black text-white px-4 py-2 text-sm hover:bg-[#002FA7] disabled:opacity-50">
            {busy ? "…" : "Add"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-semibold mono text-sm">{cfg.domain}</div>
              <div className={`text-xs mt-1 ${cfg.verified ? "text-[#059669]" : "text-[#B45309]"}`}>
                {cfg.verified ? <><ShieldCheck size={11} className="inline mr-1"/>Verified · live</> : <><AlertTriangle size={11} className="inline mr-1"/>Pending DNS verification</>}
              </div>
            </div>
            <div className="flex gap-2">
              {!cfg.verified && (
                <button onClick={verify} disabled={busy} data-testid="whitelabel-verify-button" className="border-2 border-black px-3 py-1.5 text-sm hover:bg-black hover:text-white disabled:opacity-50">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : "Verify"}
                </button>
              )}
              <button onClick={remove} disabled={busy} className="text-eyebrow text-[#A1A1AA] hover:text-[#E11D48]" data-testid="whitelabel-remove-button">Remove</button>
            </div>
          </div>
          {cfg.records?.length > 0 && (
            <div className="border border-[#E2E4E9] overflow-x-auto" data-testid="whitelabel-dns-records">
              <table className="w-full text-xs mono">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E4E9]">
                  <tr><th className="p-2 text-left">Type</th><th className="p-2 text-left">Host</th><th className="p-2 text-left">Value</th><th className="p-2"></th></tr>
                </thead>
                <tbody>
                  {cfg.records.map((r, i) => (
                    <tr key={i} className="border-b border-[#E2E4E9] last:border-0">
                      <td className="p-2 font-bold">{r.type}</td>
                      <td className="p-2 break-all">{r.name}</td>
                      <td className="p-2 break-all">{r.value}</td>
                      <td className="p-2 text-right">
                        <button onClick={() => { navigator.clipboard.writeText(r.value); toast.success("Copied"); }} className="text-[#52525B] hover:text-black"><Copy size={12}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BuzzsproutConfig({ workspace }) {
  const [cfg, setCfg] = React.useState(null);
  const [podcastId, setPodcastId] = React.useState("");
  const [token, setToken] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.buzzsproutConfig(workspace.workspace_id).then(setCfg).catch(() => {});
  }, [workspace.workspace_id]);

  const save = async () => {
    if (!podcastId.trim() || !token.trim()) {
      toast.error("Podcast ID + API token both required");
      return;
    }
    setBusy(true);
    try {
      await api.saveBuzzsprout(workspace.workspace_id, podcastId, token);
      toast.success("Buzzsprout connected. Episodes will auto-sync to Spotify + Apple Pods.");
      const next = await api.buzzsproutConfig(workspace.workspace_id);
      setCfg(next);
      setPodcastId("");
      setToken("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border-t border-[#E2E4E9] pt-5" data-testid="buzzsprout-block">
      <div className="flex items-center gap-2 mb-2">
        <Mic size={14}/>
        <span className="text-eyebrow">Podcast host (Buzzsprout)</span>
      </div>
      <p className="text-xs text-[#52525B] mb-3 leading-relaxed">
        Optional: connect Buzzsprout so audio episodes auto-publish to Spotify + Apple Podcasts.
        Get your <strong>Podcast ID</strong> from Buzzsprout dashboard URL (the number after /podcast/) and an <strong>API Token</strong> from Buzzsprout → API → Tokens.
        <a href="https://www.buzzsprout.com/help/254-what-is-the-buzzsprout-api" target="_blank" rel="noreferrer" className="underline ml-1">Docs →</a>
      </p>
      {cfg?.connected ? (
        <div className="flex items-center justify-between gap-3 border border-[#059669] bg-[#ECFDF5] p-3 text-sm" data-testid="buzzsprout-connected">
          <div>
            <div className="font-semibold">Connected · Podcast {cfg.podcast_id}</div>
            <div className="text-xs text-[#52525B] mono mt-1">Token: {cfg.token_masked}</div>
          </div>
          <button onClick={() => setCfg(null)} className="text-eyebrow text-[#A1A1AA] hover:text-[#E11D48]" data-testid="buzzsprout-reconnect">
            Replace
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Podcast ID (e.g. 2148293)"
              value={podcastId}
              onChange={(e) => setPodcastId(e.target.value)}
              data-testid="buzzsprout-podcast-id-input"
              className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none font-mono"
            />
            <input
              placeholder="API Token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              type="password"
              data-testid="buzzsprout-token-input"
              className="border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none font-mono"
            />
          </div>
          <button onClick={save} disabled={busy} data-testid="buzzsprout-save-button" className="mt-2 px-4 py-2 text-sm bg-black text-white hover:bg-[#002FA7] disabled:opacity-50">
            {busy ? "Saving…" : "Connect Buzzsprout"}
          </button>
        </>
      )}
    </div>
  );
}
